import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { PublicShareManifestResponse } from "@ultradrive/shared-types";
import { getManifest } from "./api";
import { PasswordGate } from "./PasswordGate";
import { PosterGrid } from "./PosterGrid";
import { MediaLightbox } from "./MediaLightbox";
import { BulkDownloadBar } from "./BulkDownloadBar";
import { ShareUnavailable } from "./ShareUnavailable";
import { ContributeBar } from "./ContributeBar";

// Top-level orchestrator for `/s/:token`. Fetches the manifest, gates
// on password if needed, and routes to the album view or the
// "unavailable" placeholder when the share is revoked / expired / 404.

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: PublicShareManifestResponse }
  | { kind: "error"; reason: "revoked" | "expired" | "missing" | "network" };

export function PublicSharePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setState({ kind: "error", reason: "missing" });
      return;
    }
    try {
      const data = await getManifest(token);
      setState({ kind: "ready", data });
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "SHARE_REVOKED") setState({ kind: "error", reason: "revoked" });
      else if (code === "SHARE_EXPIRED") setState({ kind: "error", reason: "expired" });
      else if (code === "SHARE_NOT_FOUND") setState({ kind: "error", reason: "missing" });
      else setState({ kind: "error", reason: "network" });
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (state.kind === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 dark:text-zinc-400">
        Loading…
      </div>
    );
  }
  if (state.kind === "error") {
    return <ShareUnavailable reason={state.reason} />;
  }

  const { share, items, requiresPassword } = state.data;

  if (requiresPassword) {
    return <PasswordGate token={token} title={share.title} note={share.note} onUnlocked={refresh} />;
  }

  const closeLightbox = () => setOpenIndex(null);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <ShareHeader title={share.title} note={share.note} itemCount={share.itemCount} totalBytes={share.totalBytes} />
      <main className="mx-auto max-w-6xl px-4 py-6">
        {share.acceptContributions ? (
          <ContributeBar token={token} onUploaded={refresh} />
        ) : null}
        {items.length === 0 ? (
          <div className="text-center text-zinc-500 dark:text-zinc-400 py-20">
            {share.acceptContributions
              ? "Be the first to share photos in this album."
              : "No files in this share yet."}
          </div>
        ) : (
          <PosterGrid
            items={items}
            onOpen={(i: number) => setOpenIndex(i)}
            token={token}
          />
        )}
        {share.allowZipBulk && share.allowDownload && items.length > 1 ? (
          <BulkDownloadBar
            token={token}
            totalBytes={share.totalBytes}
            itemCount={share.itemCount}
          />
        ) : null}
      </main>
      {openIndex !== null && items[openIndex] ? (
        <MediaLightbox
          token={token}
          items={items}
          openIndex={openIndex}
          onClose={closeLightbox}
          onChange={(i: number) => setOpenIndex(i)}
          allowDownload={share.allowDownload}
        />
      ) : null}
    </div>
  );
}

function ShareHeader(props: {
  title: string | null;
  note: string | null;
  itemCount: number;
  totalBytes: number;
}) {
  return (
    <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-950/80 backdrop-blur sticky top-0 z-10">
      <div className="mx-auto max-w-6xl px-4 py-4 flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold truncate">
            {props.title ?? "Shared with you"}
          </h1>
          {props.note ? (
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 truncate">
              {props.note}
            </p>
          ) : null}
        </div>
        <div className="text-sm text-zinc-500 dark:text-zinc-400 shrink-0">
          {props.itemCount} file{props.itemCount === 1 ? "" : "s"} · {humanBytes(props.totalBytes)}
        </div>
      </div>
    </header>
  );
}

function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}
